import React from 'react';
import { toast } from 'react-toastify';
import { Text } from '../../../components/Text/Text';
import { useSubmitContactFormMutation } from '../../../services/contacts';

export const FILE_SIZE = 5000000;
export const SUPPORTED_DOCS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg'];
export const SUPPORTED_IMAGES = ['pdf', 'jpg', 'jpeg', 'png'];

/**
 * @param file
 * @param isPhoto
 */
export function checkFileFormat(file, isPhoto = false) {
  if (!file?.name && !file?.type) return false;
  if (isPhoto) return SUPPORTED_IMAGES.includes(file.type.split('/').pop());
  if (SUPPORTED_DOCS.findIndex((el) => file.name.includes(el)) >= 0) {
    if (file.name.endsWith(SUPPORTED_DOCS[1]) || file.name.endsWith(SUPPORTED_DOCS[2])) {
      return file.type.includes('officedocument') || file.type.includes('msword');
    }
    return SUPPORTED_DOCS.includes(file.type.split('/').pop());
  }
  return false;
}

/**
 * Hook to handle contact form API requests using RTK Query
 * @param {Function} callback - Callback function to call on success
 * @param {Function} translate - Translation function
 * @param {string} language - Language code
 * @returns {Function} handleApiRequest function
 */
export function useHandleApiRequest(callback, translate, language = 'en') {
  const [submitContactForm] = useSubmitContactFormMutation();

  const notify = (message, notificationType) => {
    toast(<Text type="subtitle2">{message}</Text>, {
      notificationType,
    });
  };

  const handleApiRequest = async (url, data) => {
    if (!url || !data) {
      if (typeof callback === 'function') callback(true);
      return null;
    }

    try {
      await submitContactForm({
        formType: url,
        formData: data,
        language,
      }).unwrap();
      
      if (typeof callback === 'function') callback(true);
    } catch (err) {
      console.error(err);
      notify(translate('notifications.error'), 'error');
      if (typeof callback === 'function') callback(true);
    }
    
    return null;
  };

  return handleApiRequest;
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use useHandleApiRequest hook instead
 * @param url
 * @param data
 * @param callback
 * @param translate
 * @param language
 */
export function handleApiRequest(url, data, callback, translate, language = 'en') {
  console.warn('handleApiRequest: This function is deprecated. Use useHandleApiRequest hook instead.');
  if (!url || !data) {
    if (typeof callback === 'function') callback(true);
    return null;
  }
  // This will be replaced by hook usage in components
  return null;
}

/**
 * @param arr
 * @param key
 * @param lang
 */
export function getLabelForKey(arr, key, lang) {
  return arr[arr.findIndex((el) => el.value === key)].translationName[lang];
}

/**
 * @param data
 * @param field
 * @param file
 */
export function checkIfNoAttachment(data, field, file) {
  if (data[field]) {
    // eslint-disable-next-line immutable/no-mutation
    return {
      ...data,
      [field]: file,
    };
  }
  const { [field]: emptyField, ...restOfData } = data;
  return restOfData;
}

/**
 * @param msgSubject
*/
export function resolveTypeToApiEndpoint(msgSubject) {
  switch (msgSubject) {
    case 'career_request':
      return 'career';
    case 'maintenance':
      return 'maintenance';
    case 'complaint':
      return 'suggestions';
    case 'returns':
    case 'business_sales':
    case 'assist_online_purchase':
    case 'opportunities':
    case 'other_inquiries':
    case 'training':
      return 'generic';
    default:
      return 'corporate_partners';
  }
}
